import TagBadge from "./TagBadge";
import { isShopExpired, formatOpeningHours, getExpirationInfo, getShopStatusInfo } from "../utils/shop";
import React, { useRef, useEffect, useState, memo } from "react";
import { motion } from "framer-motion";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { Wrapper } from "@googlemaps/react-wrapper";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { Shop } from "../types/Shop";
import { createCustomMarker, createShopMarker, createCustomShopMarker, createUserLocationMarker, getResponsiveClusterSize } from "./MapMarker";
import { getOptimizedShopIcon } from "../utils/imagekit";
import { ViewportMarkerManager } from "../utils/ViewportMarkerManager";

import { getDirectionsToShop } from "../utils/directions";

interface MapViewProps {
  shops: Shop[];
  userLocation: [number, number] | null;
  isDarkMode?: boolean;
  isTrackingLocation?: boolean;
  defaultMapType?: string;
  onMapClick?: () => void;
}

// Environment variables with VITE_ prefix for frontend
const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "";
const GOOGLE_MAPS_MAP_ID = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID || "";
const GOOGLE_MAPS_MAP_ID_DARK = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID_DARK || "";

interface GoogleMapProps {
  shops: Shop[];
  userLocation: [number, number] | null;
  isDarkMode?: boolean;
  isTrackingLocation?: boolean;
  mapId: string;
  mapIdDark: string;
  defaultMapType?: string;
  onMapClick?: () => void;
}

const GoogleMap: React.FC<GoogleMapProps> = ({ shops, userLocation, isDarkMode, isTrackingLocation, mapId, mapIdDark, defaultMapType = "roadmap", onMapClick }) => {
  // Memoize filtering to prevent re-computation on every render
  const visibleShops = React.useMemo(() =>
    shops.filter(shop => !isShopExpired(shop)),
    [shops]
  );
  const ref = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map>();
  const [infoWindow, setInfoWindow] = useState<google.maps.InfoWindow>();
  const [selectedShop, setSelectedShop] = useState<Shop | null>(null);
  const [showShopDetails, setShowShopDetails] = useState(false);
  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const userMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const markerClustererRef = useRef<MarkerClusterer | null>(null);
  const viewportManagerRef = useRef<ViewportMarkerManager | null>(null);
  const currentMapIdRef = useRef<string>("");
  const hasInitialLocationRef = useRef<boolean>(false);
  const previousTrackingStateRef = useRef<boolean>(false);
  const hasPannedForTrackingRef = useRef<boolean>(false);


  // Debug: Log component mount/unmount
  useEffect(() => {
    const componentId = Math.random().toString(36).substring(2, 11);
    console.log(`🔄 [GoogleMap] Component mounted - ID: ${componentId}`, {
      hasMap: !!map,
      isDarkMode,
      userLocationExists: !!userLocation,
      shopCount: shops.length,
      theme: isDarkMode ? "dark" : "light",
    });
    return () => {
      console.log(`🗑️ [GoogleMap] Component unmounting - ID: ${componentId} (theme change)`);
    };
  }, []); // Only run on mount/unmount

  // Fix for touch event intervention errors
  useEffect(() => {
    const handleTouchMove = (e: TouchEvent) => {
      // Only prevent default if the event is cancelable
      if (e.cancelable) {
        // Let Google Maps handle the event
        return;
      }
    };

    // Add passive event listeners to avoid intervention errors
    const mapContainer = ref.current;
    if (mapContainer) {
      mapContainer.addEventListener("touchmove", handleTouchMove, { passive: true });
      return () => {
        mapContainer.removeEventListener("touchmove", handleTouchMove);
      };
    }
  }, []);

  // Handle window resize for responsive marker sizing
  useEffect(() => {
    let resizeTimeout: number;

    const handleResize = () => {
      // Debounce resize events to avoid excessive re-renders
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        console.log("📱 [MapView] Window resized, updating marker sizes");
        // Force re-render of markers by updating shops dependency
        // This will trigger the shop markers effect to recreate markers with new sizes
        if (map && markersRef.current.length > 0) {
          // The markers will be recreated with new responsive sizes
          // when the shops effect runs again
        }
      }, 250);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, [map]);

  // Create map ONCE per theme - will remount when theme changes via key prop
  useEffect(() => {
    if (ref.current && !map) {
      const center = userLocation ? { lat: userLocation[0], lng: userLocation[1] } : { lat: 55.4038, lng: 10.4024 };
      const selectedMapId = isDarkMode ? mapIdDark : mapId;
      currentMapIdRef.current = selectedMapId;

      console.log("🗺️ [MapView] Creating new Google Map instance", {
        center,
        mapId: selectedMapId,
        theme: isDarkMode ? "dark" : "light",
        reason: "theme_change_or_initial_load",
      });

      const newMap = new google.maps.Map(ref.current, {
        mapId: selectedMapId,
        center,
        zoom: 13,
        disableDefaultUI: true,
        keyboardShortcuts: false,
        gestureHandling: "greedy",
        draggable: true,
        scrollwheel: true,
        disableDoubleClickZoom: false,
        // Set initial map type based on user preference
        mapTypeId: defaultMapType as google.maps.MapTypeId,
        // Disable native map type control since we have it in dropdown
        mapTypeControl: false,
      });

      console.log("✅ [MapView] Google Map created successfully");
      setMap(newMap);
      setInfoWindow(new google.maps.InfoWindow());

      // Add click listener to close dropdown when clicking on map
      if (onMapClick) {
        newMap.addListener("click", onMapClick);
      }
    }
  }, [ref, isDarkMode, onMapClick]); // Include onMapClick in dependencies

  // Handle map type changes
  useEffect(() => {
    if (map && defaultMapType) {
      console.log("🗺️ [MapView] Changing map type to:", defaultMapType);
      map.setMapTypeId(defaultMapType as google.maps.MapTypeId);
    }
  }, [map, defaultMapType]);

  // Note: We removed the theme change effect because Google Maps doesn't allow
  // changing mapId after creation. Instead, we use a key prop to remount the component.

  // Handle user location changes - pan when tracking is re-enabled or on first location
  useEffect(() => {
    if (map && userLocation) {
      const newCenter = { lat: userLocation[0], lng: userLocation[1] };
      const isFirstLocation = !hasInitialLocationRef.current;
      const trackingJustEnabled = isTrackingLocation && !previousTrackingStateRef.current && !hasPannedForTrackingRef.current;
      const shouldPan = isFirstLocation || trackingJustEnabled;

      console.log("📍 [MapView] GPS location update received:", {
        coordinates: {
          lat: userLocation[0],
          lng: userLocation[1],
        },
        timestamp: new Date().toISOString(),
        action: userMarkerRef.current ? "updating_existing_marker" : "creating_new_marker",
        isTrackingLocation: !!isTrackingLocation,
        previousTrackingState: previousTrackingStateRef.current,
        hasPannedForTracking: hasPannedForTrackingRef.current,
        trackingJustEnabled,
        willPanToLocation: shouldPan,
      });

      // Pan to user location on first load OR when tracking is re-enabled
      if (shouldPan) {
        map.panTo(newCenter);
        if (isFirstLocation) {
          hasInitialLocationRef.current = true;
          console.log("🎯 [MapView] Panned to initial user location (first time only)");
        } else if (trackingJustEnabled) {
          hasPannedForTrackingRef.current = true;
          console.log("🎯 [MapView] Panned to user location (tracking re-enabled)");
        }
      } else {
        console.log("📍 [MapView] Updated user location without panning");
      }

      // Update or create user location marker
      if (userMarkerRef.current) {
        userMarkerRef.current.position = newCenter;
        console.log("🔄 [MapView] Updated existing user location marker" + (shouldPan ? "" : " (no pan)"));
      } else {
        const userMarkerConfig = createUserLocationMarker();
        const userMarkerElement = createCustomMarker(userMarkerConfig);

        userMarkerRef.current = new google.maps.marker.AdvancedMarkerElement({
          position: newCenter,
          map,
          title: "Your Location",
          content: userMarkerElement,
        });
        console.log("📌 [MapView] Created new user location marker");
      }

      // Update previous tracking state at the end
      previousTrackingStateRef.current = !!isTrackingLocation;
    }
  }, [map, userLocation, isTrackingLocation]);

  // Reset panning flag when tracking is disabled
  useEffect(() => {
    if (!isTrackingLocation) {
      hasPannedForTrackingRef.current = false;
      console.log("📍 [MapView] Reset panning flag - tracking disabled");
    }
  }, [isTrackingLocation]);

  // Initialize viewport manager when map is ready
  useEffect(() => {
    if (map) {
      // Initialize viewport manager
      if (!viewportManagerRef.current) {
        viewportManagerRef.current = new ViewportMarkerManager(map);
        console.log("🔍 [MapView] ViewportMarkerManager initialized");
      }

      // Update shops in viewport manager
      viewportManagerRef.current.updateShops(visibleShops);

      // Listen for viewport changes to update markers efficiently
      const handleViewportChange = (event: CustomEvent) => {
        const { visibleShops: viewportShops } = event.detail;
        console.log("🔍 [MapView] Viewport changed, visible shops:", viewportShops.length);
        // The viewport manager will handle which shops to show
        // We'll use this event to optimize marker updates if needed
      };

      window.addEventListener('viewportShopsChanged', handleViewportChange as EventListener);

      return () => {
        window.removeEventListener('viewportShopsChanged', handleViewportChange as EventListener);
      };
    }
  }, [map, visibleShops]);

  // Cleanup viewport manager on unmount
  useEffect(() => {
    return () => {
      if (viewportManagerRef.current) {
        viewportManagerRef.current.destroy();
        viewportManagerRef.current = null;
        console.log("🗑️ [MapView] ViewportMarkerManager destroyed");
      }
    };
  }, []);

  // Handle shop markers with clustering - recreate when shops change
  useEffect(() => {
    if (map && infoWindow) {
      console.log("🏪 [MapView] Updating shop markers with clustering:", {
        shopCount: shops.length,
        timestamp: new Date().toISOString(),
        action: shops.length > 0 ? "recreating_clustered_markers" : "clearing_all_markers",
      });

      // Clear existing clusterer and markers
      if (markerClustererRef.current) {
        markerClustererRef.current.clearMarkers();
        markerClustererRef.current = null;
      }
      markersRef.current.forEach((marker) => (marker.map = null));
      markersRef.current = [];

      // Add shop markers only if there are shops to show
      if (visibleShops.length > 0) {
        const newMarkers: google.maps.marker.AdvancedMarkerElement[] = [];

        visibleShops.forEach((shop) => {
          const markerConfig = shop.isCustom ? createCustomShopMarker(shop) : createShopMarker(shop);
          const shopMarkerElement = createCustomMarker(markerConfig);

          const marker = new google.maps.marker.AdvancedMarkerElement({
            position: { lat: shop.coordinates[0], lng: shop.coordinates[1] },
            map: null, // Don't add to map directly, let clusterer handle it
            title: shop.name,
            content: shopMarkerElement,
          });

          // Enhanced info window with time information
          marker.addListener("click", () => {
            const { currentlyOpen, statusText, timeInfo } = getShopStatusInfo(shop);

            // Create scoped functions to avoid global pollution
            const handleDirections = () => getDirectionsToShop(shop);
            const handleExpandDetails = () => {
              setSelectedShop(shop);
              setShowShopDetails(true);
              infoWindow.close();
            };

            // Store functions temporarily for this info window only
            const tempFunctions = {
              [`getDirections_${shop.id}`]: handleDirections,
              [`expandDetails_${shop.id}`]: handleExpandDetails
            };

            // Add to window temporarily
            Object.assign(window as any, tempFunctions);

            // Clean up when info window closes
            const originalClose = infoWindow.close;
            infoWindow.close = function () {
              // Remove temporary functions
              Object.keys(tempFunctions).forEach(key => {
                delete (window as any)[key];
              });
              // Restore original close method
              infoWindow.close = originalClose;
              // Call original close
              originalClose.call(this);
            };
            // Add expiration info for custom shops (debugging)
            let expirationInfo = "";
            if (shop.isCustom && shop.expiresAt) {
              const now = new Date();
              const expiresAt = new Date(shop.expiresAt);
              const timeLeft = expiresAt.getTime() - now.getTime();
              if (timeLeft <= 0) {
                expirationInfo = `<div style="font-size: 12px; color: #d93025; margin-top: 4px; padding: 4px; background-color: #ffeaea; border-radius: 4px;">⚠️ Expired</div>`;
              } else {
                const hours = Math.floor(timeLeft / (1000 * 60 * 60));
                const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
                const timeText = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
                expirationInfo = `<div style="font-size: 12px; color: #f57c00; margin-top: 4px; padding: 4px; background-color: #fff8e1; border-radius: 4px;">⏰ Ending in ${timeText}</div>`;
              }
            }
            // Get optimized shop icon for popup
            const popupIconUrl = shop.iconUrl ? getOptimizedShopIcon.large(shop.iconUrl) : null;
            const iconHtml = popupIconUrl
              ? `<img src="${popupIconUrl}" alt="${shop.name} logo" style="width: 32px; height: 32px; object-fit: contain; border-radius: 4px; margin-right: 8px; flex-shrink: 0;" loading="lazy" onerror="this.style.display='none';" />`
              : '';

            const basicInfoContent = `
              <div style="display: flex; align-items: center; margin-bottom: 8px;">
                ${iconHtml}
                <div>
                  <div style="font-size: 16px; font-weight: 500; color: #3c4043;">${shop.name}</div>
                  <div style="font-size: 14px; color: #70757a;">${shop.address}</div>
                </div>
              </div>
              <div style="font-size: 12px; color: ${currentlyOpen ? "#137333" : "#d93025"}; margin-top: 4px;">
                ${statusText}${timeInfo ? ` • ${timeInfo}` : ""}
              </div>
              ${expirationInfo}
              <div style="display: flex; gap: 8px; margin-top: 8px;">
                <button 
                  onclick="window.getDirections_${shop.id}()"
                  style="
                    padding: 6px 12px;
                    background-color: #1976d2;
                    color: white;
                    border: none;
                    border-radius: 4px;
                    font-size: 12px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 4px;
                  "
                  onmouseover="this.style.backgroundColor='#1565c0'"
                  onmouseout="this.style.backgroundColor='#1976d2'"
                >
                  🧭 Directions
                </button>
                
                <button 
                  onclick="window.expandDetails_${shop.id}()"
                  style="
                    padding: 6px 12px;
                    background-color: #28a745;
                    color: white;
                    border: none;
                    border-radius: 4px;
                    font-size: 12px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 4px;
                  "
                  onmouseover="this.style.backgroundColor='#218838'"
                  onmouseout="this.style.backgroundColor='#28a745'"
                >
                  📋 Details
                </button>
              </div>
            `;
            infoWindow.setContent(basicInfoContent);
            infoWindow.open(map, marker);
          });

          newMarkers.push(marker);
        });

        // Store markers reference
        markersRef.current = newMarkers;

        // Create clusterer with custom styling
        markerClustererRef.current = new MarkerClusterer({
          markers: newMarkers,
          map,
          renderer: {
            render: ({ count, position }) => {
              // Custom cluster marker styling with responsive sizing
              const color = count > 10 ? "#d32f2f" : count > 5 ? "#f57c00" : "#1976d2";
              const size = getResponsiveClusterSize(count);

              return new google.maps.marker.AdvancedMarkerElement({
                position,
                content: (() => {
                  const div = document.createElement("div");
                  div.style.cssText = `
                    background-color: ${color};
                    color: white;
                    width: ${size}px;
                    height: ${size}px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: bold;
                    font-size: ${size > 45 ? "14px" : "12px"};
                    border: 3px solid white;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                    cursor: pointer;
                    transition: transform 0.2s ease;
                  `;
                  div.textContent = count.toString();

                  // Add hover effect
                  div.addEventListener("mouseenter", () => {
                    div.style.transform = "scale(1.1)";
                  });
                  div.addEventListener("mouseleave", () => {
                    div.style.transform = "scale(1)";
                  });

                  return div;
                })(),
                zIndex: 1000,
              });
            },
          },
        });

        console.log(`✅ [MapView] Created ${shops.length} clustered shop markers successfully`);
      } else {
        console.log("✅ [MapView] All shop markers cleared (no shops to display)");
      }
    }
  }, [map, infoWindow, shops]);

  // Cleanup markers and clusterer on unmount
  useEffect(() => {
    return () => {
      if (markerClustererRef.current) {
        markerClustererRef.current.clearMarkers();
        markerClustererRef.current = null;
      }
      markersRef.current.forEach((marker) => (marker.map = null));
      if (userMarkerRef.current) {
        userMarkerRef.current.map = null;
      }

      // Reset initial location flag when component unmounts (theme change)
      hasInitialLocationRef.current = false;
      hasPannedForTrackingRef.current = false;
    };
  }, []);

  // ...removed local formatOpeningHours, now using from utils/shop



  // ...removed local getExpirationInfo, now using from utils/shop

  return (
    <>
      <div
        ref={ref}
        className="h-full w-full"
        style={{
          touchAction: "none", // Let Google Maps handle ALL touch events
          WebkitTouchCallout: "none",
          WebkitUserSelect: "none",
          userSelect: "none",
          WebkitTapHighlightColor: "transparent",
        }}
      />

      {/* Shop Details Modal */}
      {showShopDetails && selectedShop && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-4">
          <div className={`w-full max-w-md rounded-t-2xl md:rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto ${isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900"
            }`}>
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold">{selectedShop.name}</h3>
              <button
                onClick={() => setShowShopDetails(false)}
                className="p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Close shop details"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
              {/* Status */}
              {(() => {
                const { currentlyOpen, statusText, timeInfo } = getShopStatusInfo(selectedShop);
                return (
                  <div className={`text-sm font-medium ${currentlyOpen ? "text-green-600" : "text-red-600"}`}>
                    {statusText}{timeInfo ? ` • ${timeInfo}` : ""}
                  </div>
                );
              })()}

              {/* Expiration Info */}
              {(() => {
                const expirationInfo = getExpirationInfo(selectedShop);
                if (!expirationInfo) return null;
                return (
                  <div className={`text-sm px-3 py-2 rounded-lg ${expirationInfo.expired
                    ? "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400"
                    : "bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400"
                    }`}>
                    ⏰ {expirationInfo.text}
                  </div>
                );
              })()}

              {/* Details Grid */}
              <div className="space-y-3">
                <div>
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Type</span>
                  <p className="text-sm">{selectedShop.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                </div>

                <div>
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Address</span>
                  <p className="text-sm">{selectedShop.address}</p>
                </div>

                <div>
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Coordinates</span>
                  <p className="text-sm font-mono">{selectedShop.coordinates[0].toFixed(6)}, {selectedShop.coordinates[1].toFixed(6)}</p>
                </div>

                {selectedShop.phone && (
                  <div>
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Phone</span>
                    <p className="text-sm">
                      <a href={`tel:${selectedShop.phone}`} className="text-blue-600 hover:underline">
                        {selectedShop.phone}
                      </a>
                    </p>
                  </div>
                )}

                {selectedShop.website && (
                  <div>
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Website</span>
                    <p className="text-sm">
                      <a href={selectedShop.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        {selectedShop.website}
                      </a>
                    </p>
                  </div>
                )}

                {selectedShop.description && (
                  <div>
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Description</span>
                    <p className="text-sm">{selectedShop.description}</p>
                  </div>
                )}

                {selectedShop.rating && (
                  <div>
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Rating</span>
                    <p className="text-sm">{selectedShop.rating}/5 ⭐</p>
                  </div>
                )}

                {selectedShop.tags && selectedShop.tags.length > 0 && (
                  <div>
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Tags</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedShop.tags.map((tag, index) => (
                        <TagBadge key={index} tag={tag} />
                      ))}
                    </div>
                  </div>
                )}

                {selectedShop.distance && (
                  <div>
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Distance</span>
                    <p className="text-sm">{selectedShop.distance.toFixed(2)} km</p>
                  </div>
                )}

                {selectedShop.openingHours && selectedShop.openingHours.length > 0 && (
                  <div>
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Opening Hours</span>
                    <div className="mt-1 space-y-1">
                      {formatOpeningHours(selectedShop.openingHours).map((hours, index) => (
                        <p key={index} className="text-sm">{hours}</p>
                      ))}
                    </div>
                  </div>
                )}

                {selectedShop.isCustom && (
                  <div>
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Custom Shop Details</span>
                    <div className="mt-1 space-y-1 text-sm">
                      {selectedShop.createdByUserId && (
                        <p>Created by: {selectedShop.createdByUserId}</p>
                      )}
                      {selectedShop.expiresAt && (
                        <p>Expires: {new Date(selectedShop.expiresAt).toLocaleString()}</p>
                      )}
                      {selectedShop.lifetimeHours && (
                        <p>Lifetime: {selectedShop.lifetimeHours} hours</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => getDirectionsToShop(selectedShop)}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                >
                  🧭 Directions
                </button>
                <button
                  onClick={() => setShowShopDetails(false)}
                  className="px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const MapView: React.FC<MapViewProps> = memo(({ shops, userLocation, isDarkMode, isTrackingLocation, defaultMapType = "roadmap", onMapClick }) => {
  const { shouldReduceMotion, animationConfig } = useReducedMotion();
  const [loading, setLoading] = useState(true);

  // Debug: Log only when props actually change (not on every render)
  useEffect(() => {
    console.log("🌐 [MapView] Props changed", {
      isDarkMode,
      userLocationExists: !!userLocation,
      shopCount: shops.length,
      timestamp: new Date().toISOString(),
    });
  }, [isDarkMode, userLocation, shops.length]); // Only log when these actually change

  useEffect(() => {
    // Validate that we have the required configuration from environment variables
    if (!GOOGLE_MAPS_API_KEY) {
      console.warn("Google Maps API key not found. Map will be disabled.");
      // Don't throw, let it render fallback
    }

    setLoading(false);
  }, []);

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="h-full w-full flex items-center justify-center bg-transparent"
      >
        <motion.div
          initial={shouldReduceMotion ? { opacity: 1 } : { scale: 0.8, opacity: 0 }}
          animate={shouldReduceMotion ? { opacity: 1 } : { scale: 1, opacity: 1 }}
          transition={shouldReduceMotion ? { duration: 0 } : { delay: 0.1, duration: animationConfig.duration }}
          className="text-center"
        >
          <motion.div
            animate={shouldReduceMotion ? {} : { rotate: 360 }}
            transition={shouldReduceMotion ? { duration: 0 } : { duration: 1, repeat: Infinity, ease: "linear" }}
            className={`rounded-full h-8 w-8 border-3 border-blue-200 border-t-blue-600 mx-auto mb-2 ${shouldReduceMotion ? 'gpu-accelerated' : ''}`}
          ></motion.div>
          <motion.p
            initial={shouldReduceMotion ? { opacity: 1 } : { y: 5, opacity: 0 }}
            animate={shouldReduceMotion ? { opacity: 1 } : { y: 0, opacity: 1 }}
            transition={shouldReduceMotion ? { duration: 0 } : { delay: 0.3 }}
            className="text-gray-600 text-sm"
          >
            Loading map...
          </motion.p>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <div
      className="h-full w-full"
      style={{
        touchAction: "none", // Let Google Maps handle ALL touch events
        WebkitOverflowScrolling: "touch",
        position: "relative",
      }}
    >
      {GOOGLE_MAPS_API_KEY ? (
        <Wrapper apiKey={GOOGLE_MAPS_API_KEY} libraries={["marker", "places"]}>
          <GoogleMap
            key={isDarkMode ? "dark" : "light"}
            shops={shops}
            userLocation={userLocation}
            isDarkMode={isDarkMode}
            isTrackingLocation={isTrackingLocation}
            mapId={GOOGLE_MAPS_MAP_ID}
            mapIdDark={GOOGLE_MAPS_MAP_ID_DARK}
            defaultMapType={defaultMapType}
            onMapClick={onMapClick}
          />
        </Wrapper>
      ) : (
        <div className="h-full w-full flex items-center justify-center bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 p-4 text-center">
          <div>
            <p className="font-semibold mb-2">Map Unavailable</p>
            <p className="text-sm">Google Maps API key is missing.</p>
          </div>
        </div>
      )}
    </div>
  );
});

export default MapView;
